import { sanitizeAnalyticsUrl } from '@/utilities/sanitizeAnalyticsUrl'

/**
 * GA4 ecommerce event helpers
 * These safely route to the global gtag function.
 */

function gtag(...args: any[]) {
  if (typeof window === 'undefined') return

  if (typeof (window as any).gtag === 'function') {
    // Library is loaded and ready — call directly
    ;(window as any).gtag(...args)
  } else {
    // gtag/js not yet loaded — push to dataLayer queue so the event
    // is replayed once the library initialises (prevents silent drops)
    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).dataLayer.push(args)
  }
}

// --- buildGAItems (shared helper) ----------------------------------------
/** Convert raw cart items array into GA4 items array */
export function buildGAItems(cartItems: any[]): object[] {
  return cartItems.map((item: any) => {
    const product = item.product
    const price =
      item.selectedVariantData?.priceTotal ??
      product?.basePrice ??
      0
    const categories = product?.categories?.filter((c: any) => typeof c === 'object') ?? []
    const variantLabel = item.selectedVariantData?.options
      ?.map((o: any) => `${o.name}: ${o.value}`)
      .join(', ')
    return {
      item_id: String(product?.id ?? ''),
      item_name: product?.title ?? '',
      price,
      quantity: item.quantity ?? 1,
      ...(variantLabel ? { item_variant: variantLabel } : {}),
      ...(categories[0] ? { item_category: categories[0].title } : {}),
    }
  })
}

// --- view_item ------------------------------------------------------------
/** Fire on product detail page mount */
export function trackViewItem(product: any, price: number, currency = 'EUR') {
  const categories = product.categories?.filter((c: any) => typeof c === 'object') ?? []
  gtag('event', 'view_item', {
    currency,
    value: price,
    items: [
      {
        item_id: String(product.id),
        item_name: product.title,
        price,
        quantity: 1,
        ...(categories[0] ? { item_category: categories[0].title } : {}),
      },
    ],
  })
}

// --- add_to_cart ----------------------------------------------------------
/** Fire when an item is successfully added to the cart */
export function trackAddToCart(
  product: any,
  price: number,
  quantity = 1,
  variantLabel?: string,
) {
  const categories = product.categories?.filter((c: any) => typeof c === 'object') ?? []
  gtag('event', 'add_to_cart', {
    currency: 'EUR',
    value: price * quantity,
    items: [
      {
        item_id: String(product.id),
        item_name: product.title,
        price,
        quantity,
        ...(variantLabel ? { item_variant: variantLabel } : {}),
        ...(categories[0] ? { item_category: categories[0].title } : {}),
      },
    ],
  })
}

// --- begin_checkout -------------------------------------------------------
/** Fire when the user lands on the checkout page */
export function trackBeginCheckout(cartItems: any[], totalEur: number) {
  gtag('event', 'begin_checkout', {
    currency: 'EUR',
    value: totalEur,
    items: buildGAItems(cartItems),
  })
}

// --- purchase -------------------------------------------------------------
/** Fire on the order confirmation page after a successful order */
export function trackPurchase({
  orderId,
  totalEur,
  shippingEur = 0,
  cartItems = [],
  isNewCustomer = false, // Optional: flag if the user is a new customer
}: {
  orderId: string
  totalEur: number
  shippingEur?: number
  cartItems?: any[]
  isNewCustomer?: boolean
}) {
  // 1. Standard GA4 ecommerce purchase event
  gtag('event', 'purchase', {
    transaction_id: orderId,
    currency: 'EUR',
    value: totalEur,
    shipping: shippingEur,
    items: buildGAItems(cartItems),
  })

  // 2. Google Ads specific conversion event
  gtag('event', 'conversion', {
    send_to: 'AW-18103701922/Rkt7CPzhhq0cEKKjwrhD',
    transaction_id: orderId,
    value: totalEur,
    currency: 'EUR',
    ...(isNewCustomer !== undefined ? { new_customer: isNewCustomer } : {}),
  })
}

// --- page_view (explicit, for 404 + recovery) -----------------------------
/**
 * P0-2 (doc 046): fire an explicit `page_view` event with a hard-coded
 * `page_location` to recover GA4 sessions that were being recorded with
 * `(not set)` or empty landing pages — 67 sessions/month, mostly organic
 * search and the root `/` redirect chain. The auto page_view fired by
 * `gtag('config', …)` reads `document.location` at the time the library
 * loads, which on the apex → www → /de redirect chain can be blank or
 * stale. Sending an explicit event with `page_location` set from
 * `window.location.href` (after the redirects have resolved) guarantees
 * GA4 sees the final URL.
 *
 * Use this only when `gtag('config', …)` was called with
 * `send_page_view: false` (see layout.tsx) — otherwise it will
 * double-count the page view. Today it is the only caller, the 404 page.
 */
export function trackPageView(options?: {
  pageLocation?: string
  pageTitle?: string
  pageReferrer?: string
}) {
  if (typeof window === 'undefined') return
  const location = sanitizeAnalyticsUrl(options?.pageLocation ?? window.location.href)
  const payload: Record<string, string> = {
    page_location: location,
  }
  if (options?.pageTitle) payload.page_title = options.pageTitle
  if (options?.pageReferrer) payload.page_referrer = sanitizeAnalyticsUrl(options.pageReferrer)
  gtag('event', 'page_view', payload)
}
